import { useTranslation } from "react-i18next";
import type { IconType } from "../icon";
import { useAppTheme } from "../useAppTheme";
import CustomChip from "./CustomChip";
import TooltipWrapper from "./TooltipWrapper";

export type PremiumChipProps = {
	variant: "premium" | "free";
	text?: string;
	tooltip?: string;
	onPress?: () => void;
	disabled?: boolean;
};

const VARIANT_CONFIG = {
	premium: {
		icon: "crown-outline",
		labelKey: "premium.chip",
	},
	free: {
		icon: "gift-outline",
		labelKey: "dashboard.diagnostics.free",
	},
} satisfies Record<
	PremiumChipProps["variant"],
	{ icon: IconType; labelKey: string }
>;

export const PremiumChip = ({
	variant,
	text,
	tooltip,
	onPress,
	disabled,
}: PremiumChipProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { icon, labelKey } = VARIANT_CONFIG[variant];
	const isPremium = variant === "premium";

	const textColor = isPremium
		? theme.colors.onTertiaryContainer
		: theme.colors.primary;

	const chip = (
		<CustomChip
			icon={icon}
			iconColor={textColor}
			onPress={onPress}
			disabled={disabled}
			style={{
				backgroundColor: isPremium
					? theme.colors.tertiaryContainer
					: theme.colors.surface,
				alignSelf: "center",
				opacity: disabled ? 0.5 : 1,
				borderWidth: isPremium ? 0 : 1,
				borderColor: theme.colors.primary,
			}}
			textStyle={{ color: textColor, fontWeight: 600 }}
		>
			{text ?? t(labelKey)}
		</CustomChip>
	);

	if (tooltip) {
		return <TooltipWrapper title={tooltip}>{chip}</TooltipWrapper>;
	}

	return chip;
};
